import type { ReactNode } from 'react';
import { PLAN_FEATURE_CATALOG, type PublicPlan } from '@neuroedge/shared';
import { Card } from '../ui';

export function money(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export function limitLabel(n: number | null): string {
  return n === null ? 'Unlimited' : n.toLocaleString();
}

const catalogByKey = new Map(PLAN_FEATURE_CATALOG.map((f) => [f.key, f]));

/**
 * One plan as a card: price, limits, included features and menus. Shared by
 * Billing (with upgrade actions), the in-app "Plans" page and the public
 * pricing page; \`children\` is the card's action area.
 */
export function PlanCard({
  plan,
  highlighted = false,
  children,
}: {
  plan: PublicPlan;
  /** e.g. the workspace's current plan. */
  highlighted?: boolean;
  children?: ReactNode;
}) {
  const included = plan.features.filter((f) => f.value);
  return (
    <Card
      style={{
        border: highlighted ? '2px solid var(--color-primary)' : undefined,
        position: 'relative',
      }}
    >
      {plan.isMostPopular ? (
        <span
          className="ne-badge"
          style={{ position: 'absolute', top: 'var(--space-2)', right: 'var(--space-2)' }}
        >
          MOST POPULAR
        </span>
      ) : null}
      <h3 style={{ margin: '0 0 var(--space-1)' }}>{plan.name}</h3>
      {plan.description ? (
        <p
          style={{
            margin: '0 0 var(--space-2)',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {plan.description}
        </p>
      ) : null}
      <p style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>
        {plan.isCustomPricing ? 'Custom' : `${money(plan.monthlyFeeAmount, plan.currency)}/mo`}
      </p>
      <dl style={{ margin: '0 0 var(--space-3)', display: 'grid', gap: 'var(--space-1)' }}>
        {(
          [
            ['WhatsApp numbers', plan.maxWhatsappNumbers],
            ['Team users', plan.maxTeamUsers],
            ['Contacts', plan.maxContacts],
            ['AI docs / month', plan.aiDocsIncludedPerMonth],
          ] as const
        ).map(([label, value]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between' }}>
            <dt style={{ color: 'var(--color-text-muted)' }}>{label}</dt>
            <dd style={{ margin: 0 }}>{limitLabel(value)}</dd>
          </div>
        ))}
      </dl>
      <ul
        style={{
          margin: '0 0 var(--space-3)',
          padding: 0,
          listStyle: 'none',
          display: 'grid',
          gap: '2px',
        }}
      >
        {included.map((f) => (
          <li key={f.key} style={{ fontSize: 'var(--font-size-sm)' }}>
            ✓ {catalogByKey.get(f.key)?.label ?? f.key}
          </li>
        ))}
      </ul>
      {plan.menus?.length ? (
        <p style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--font-size-sm)' }}>
          <strong>Menus:</strong> {plan.menus.map((m) => m.label).join(', ')}
        </p>
      ) : null}
      {children}
    </Card>
  );
}

/** Responsive grid of plan cards. */
export function PlanGrid({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 'var(--space-3)',
      }}
    >
      {children}
    </div>
  );
}
